import React, { useState, useRef, useEffect } from 'react';
import { BsSearch, BsArrowCounterclockwise, BsPlus, BsDownload, BsFilter } from 'react-icons/bs';
import DateFilter from './DateFilter';
import ColumnFilter from './ColumnFilter';

const Filters = ({
  onSearch,
  onRefresh,
  onAdd,
  onExport,
  placeholder = 'Search...',
  addButtonText = 'Add New',
  exportButtonText = 'Export',
  addButtonClassName = '',
  exportOptions = [
    { label: 'CSV', value: 'csv' },
    { label: 'Excel', value: 'excel' },
    { label: 'PDF', value: 'pdf' }
  ],
  filterOptions = [],
  onFilterChange,
  activeFilters = [],
  showDateFilter = false,
  onDateChange,
  dateFilterProps = {},
  showSearch = true,
  showClearFilter = true,
  headers = [],
  onColumnFilterChange,
  columnFilters = {},
  filterButtonText = 'Filter'
}) => {
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const exportRef = useRef(null);
  const filterDropdownRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setShowExportOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add new useEffect for filter dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    onSearch(value);
  };

  const handleReset = () => {
    setSearchValue('');
    onRefresh();
  };

  const handleExport = (format) => {
    if (onExport) {
      onExport(format);
    }
    setShowExportOptions(false);
  };

  const handleFilterToggle = (filter) => {
    if (onFilterChange) {
      const newFilters = activeFilters.includes(filter)
        ? activeFilters.filter(f => f !== filter)
        : [...activeFilters, filter];
      onFilterChange(newFilters);
    }
  };

  return (
    <div className="w-full mb-4">
      <div className="flex justify-between gap-3">
        {/* Left side controls: Search, Date Filter, Filter, Reset */}
        <div className="flex flex-row gap-3">
          {/* Search Input */}
          {showSearch && (
          <div className="relative flex-1 min-w-0">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <BsSearch className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className='w-full pr-8 py-2 pl-8 shadow-sm font-lufga text-xs focus:outline-none rounded-md border focus:ring-1 focus:ring-gray-300'
              placeholder={placeholder}
              value={searchValue}
              onChange={handleSearch}
            />
          </div>
          )}

          {/* Filter Controls */}
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
            {/* Date Filter */}
            {showDateFilter && (
              <DateFilter
                onDateChange={onDateChange}
                {...dateFilterProps}
              />
            )}

            {/* Column Filter */}
            {headers.length > 0 && (
              <ColumnFilter
                headers={headers}
                onFilterChange={onColumnFilterChange}
                activeFilters={columnFilters}
              />
            )}

            {/* Filter Button */}
            {filterOptions.length > 0 && (
              <div className="relative" ref={filterDropdownRef}>
                <button
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  className={`inline-flex items-center h-8 px-3 py-1 text-xs border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${activeFilters.length > 0 ? 'bg-blue-50' : ''}`}
                >
                  <BsFilter className="h-4 w-4 mr-1.5" />
                  {filterButtonText}
                  {activeFilters.length > 0 && (
                    <span className="ml-1.5 bg-blue-100 text-blue-800 text-xs font-medium px-1.5 py-0.5 rounded-full">
                      {activeFilters.length}
                    </span>
                  )}
                </button>

                {showFilterDropdown && (
                  <div className="absolute right-0 mt-1 w-fit rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 overflow-hidden sm:origin-top-right origin-top-left">
                    <div className="py-1 max-h-[60vh] overflow-y-auto" role="menu">
                      {filterOptions.map((option) => (
                        <div key={option.value} className="px-3 py-1.5 hover:bg-gray-50 transition-colors duration-150">
                          <label className="flex items-center space-x-2 cursor-pointer whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={activeFilters.includes(option.value)}
                              onChange={() => handleFilterToggle(option.value)}
                              className="h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-700">{option.label}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reset Button */}
            {showClearFilter && (
            <button
              onClick={handleReset}
              className={`inline-flex items-center justify-center h-8 w-8 focus:outline-none rounded-md border focus:ring-1 focus:ring-gray-300 ${
                activeFilters.length > 0
                  ? 'text-red-700 bg-red-50 hover:bg-red-100'
                  : 'text-gray-700 bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <BsArrowCounterclockwise className="h-4 w-4" />
            </button>
            )}
          </div>
        </div>

        {/* Right side controls: Add, Export */}
        <div className='flex flex-row gap-3'>
            {/* Add Button */}
            {onAdd && (
          <button
            onClick={onAdd}
            className={`flex items-center justify-center gap-1 h-8 w-8 lg:w-fit text-white text-sm px-1 py-1.5 lg:px-2 lg:py-1.5 focus:outline-none rounded-md border focus:ring-1 focus:ring-gray-300 ${addButtonClassName}`}
          >
            <BsPlus className="h-4 w-4" />
            <span className="hidden lg:inline">{addButtonText}</span>
          </button>
        )}     

        {/* Export Button */}
        <div className="relative" ref={exportRef}>
          <button
            type="button"
            onClick={() => setShowExportOptions(!showExportOptions)}
            className="inline-flex items-center justify-center gap-1 px-2 py-1 focus:outline-none rounded-md border focus:ring-1 focus:ring-gray-300 text-xs h-8"
          >
            <BsDownload className="h-4 w-4" />
            <span className="hidden lg:inline">{exportButtonText}</span>
          </button>
          {showExportOptions && (
            <div className="absolute right-0 mt-1 min-w-[120px] w-fit rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 overflow-hidden sm:origin-top-right origin-top-left">
              <div className="py-1 max-h-[60vh] overflow-y-auto" role="menu" aria-orientation="vertical">
                {exportOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleExport(option.value)}
                    className="block w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none transition-colors duration-150 whitespace-nowrap"
                    role="menuitem"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
</div>
      </div>
    </div>
  );
};

export default Filters; 